import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Button as MButton,
  Radio,
  RadioGroup,
  Box,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import ThumbUp from "@mui/icons-material/ThumbUp";
import ThumbDown from "@mui/icons-material/ThumbDown";

import { s360ConfigService } from "../../../Services/S360Config";
import apiService from "../../../Services/APIService";

function isValidUrl(string) {
  if (!string) return false;
  try {
    const url = new URL(string);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
}

const StyledButtonBase = styled(MButton, {
  shouldForwardProp: (prop) =>
    !["config", "configPrimaryFontColor", "configprimaryColor"].includes(prop),
})(({ theme, config, configPrimaryFontColor, configprimaryColor }) => ({
  backgroundColor: `${configprimaryColor} !important`,
  color: `${configPrimaryFontColor} !important`,
  width: config?.customDesign?.button?.width ?? "fit-content",
  minWidth: "100px",
  borderRadius: config?.customDesign?.button?.borderRadius ?? "30px !important",
  border: "2px solid #d9d9d9 !important",
  margin: "10px",
  padding: "10px 15px",
  fontSize: "12px",
  fontFamily: "'Lato', sans-serif",
  textAlign: "center",
  cursor: "pointer",
  // textTransform: "capitalize",
  textTransform: "none !important",
  transition:
    "background-color 0.3s ease-in-out, color 0.3s, border-color 0.3s, box-shadow 0.3s ease-in-out",
  boxShadow: "none",
  "&:hover": {
    color: `${configprimaryColor} !important`,
    backgroundColor: `${configPrimaryFontColor} !important`,
    border: `2px solid ${configprimaryColor} !important`,
    boxShadow: "none",
  },
  "&:disabled": {
    backgroundColor: "#fff !important",
    color: "#b3b3b3",
    borderColor: "#e0e0e0",
    opacity: "0.6",
    cursor: "not-allowed",
    boxShadow: "none",
  },
}));

const StyledSubmitButton = styled(StyledButtonBase)(({ theme }) => ({
  width: "200px !important",
}));

const StyledFeedbackButton = styled(MButton)(({ theme }) => ({
  backgroundColor: "#fff !important",
  border: "1px solid #d9d9d9 !important",
  borderRadius: "30px !important",
  color: "#333 !important",
  width: "fit-content",
  minWidth: "80px",
  padding: "10px 15px",
  fontSize: "12px",
  fontWeight: "bold",
  fontFamily: "'Lato', sans-serif",
  textAlign: "center",
  cursor: "pointer",
  textTransform: "capitalize",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-evenly",
  boxShadow: "none",
  lineHeight: "1",
  transition:
    "background-color 0.3s ease, border-color 0.3s, transform 0.1s ease",
  "&:hover": {
    backgroundColor: "#f0f0f0 !important",
    boxShadow: "none",
  },
  "&:active": {
    transform: "scale(0.98)",
  },
  "&:disabled": {
    backgroundColor: "#f8f8f8 !important",
    color: "#b3b3b3 !important",
    borderColor: "#e0e0e0 !important",
    opacity: 1,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  "& > *:first-child": {
    marginBottom: "4px",
  },
}));

const StyledCustomIcon = styled("img")({
  width: 24,
  height: 24,
  borderRadius: "0px",
});

const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) =>
    !["config", "configPrimaryFontColor", "configprimaryColor"].includes(prop),
})(({ theme, config, configPrimaryFontColor, configprimaryColor }) => ({
  width: "100%",
  maxWidth: "30rem",
  color: `${configPrimaryFontColor} !important`,
  backgroundColor: `${configprimaryColor} !important`,
  borderRadius: config?.customDesign?.button?.borderRadius ?? "4px",
  border: `1px solid ${configPrimaryFontColor} !important`,
  padding: "4px 8px",
  margin: "5px 0",
  transition: "background-color 0.3s ease",
  "&.Mui-disabled": {
    opacity: 0.6,
    cursor: "not-allowed",
  },
}));

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => !["configPrimaryFontColor"].includes(prop),
})(({ theme, configPrimaryFontColor }) => ({
  color: `${configPrimaryFontColor} !important`,
  "&.Mui-checked": {
    color: `${configPrimaryFontColor} !important`,
  },
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04) !important",
  },
  "&.Mui-disabled": {
    color: `#b3b3b3 !important`,
  },
}));

const StyledRadio = styled(Radio, {
  shouldForwardProp: (prop) => !["configPrimaryFontColor"].includes(prop),
})(({ theme, configPrimaryFontColor }) => ({
  color: `${configPrimaryFontColor} !important`,
  "&.Mui-checked": {
    color: `${configPrimaryFontColor} !important`,
  },
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04) !important",
  },
  "&.Mui-disabled": {
    color: `#b3b3b3 !important`,
  },
}));

const DefaultButton = React.memo(
  function DefaultButtonComponent({
    text,
    value,
    backgroundMedia,
    buttonLink,
    iconMedia,
    componentId,
    visible,
    sendMessage,
    disabled,
    config,
    id,
  }) {
    const configPrimaryFontColor = useMemo(
      () =>
        config?.customDesign?.button?.color ||
        s360ConfigService.config?.configDetails?.primaryColor ||
        "#015fe",
      [config]
    );
    const configprimaryColor = useMemo(
      () =>
        config?.customDesign?.button?.backgroundColor ||
        s360ConfigService.config?.configDetails?.primaryFontColor ||
        "#fff",
      [config]
    );

    const formattedButtonLink = useCallback((buttonLink) => {
      if (!buttonLink) return "";
      let url = buttonLink.trim();
      if (!/^https?:\/\//i.test(url)) {
        url = `https://${url}`;
      }
      return encodeURI(url);
    }, []);

    const handleClick = useCallback(() => {
      if (buttonLink && isValidUrl(buttonLink)) {
        window.open(formattedButtonLink(buttonLink), "_blank");
      } else {
        sendMessage(text, componentId);
      }
    }, [buttonLink, text, componentId, sendMessage, formattedButtonLink]);

    if (!visible) {
      return null;
    }

    return (
      <StyledButtonBase
        onClick={handleClick}
        className="buttonPlain"
        config={config}
        configPrimaryFontColor={configPrimaryFontColor}
        configprimaryColor={configprimaryColor}
        startIcon={
          isValidUrl(iconMedia) ? (
            <StyledCustomIcon src={iconMedia} alt="icon" />
          ) : null
        }
        variant="contained"
        disabled={disabled}
        sx={{
          backgroundImage: `url(${
            isValidUrl(backgroundMedia) ? backgroundMedia : ""
          })`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {text}
      </StyledButtonBase>
    );
  },
  (prevProps, nextProps) =>
    prevProps.text === nextProps.text &&
    prevProps.value === nextProps.value &&
    prevProps.backgroundMedia === nextProps.backgroundMedia &&
    prevProps.iconMedia === nextProps.iconMedia &&
    prevProps.buttonLink === nextProps.buttonLink &&
    prevProps.componentId === nextProps.componentId &&
    prevProps.visible === nextProps.visible &&
    prevProps.disabled === nextProps.disabled &&
    JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config)
);

const CheckboxButton = React.memo(
  function CheckboxButtonComponent({
    text,
    componentId,
    selected,
    onChange,
    disabled,
    config,
    id,
  }) {
    const configPrimaryFontColor = useMemo(
      () =>
        config?.customDesign?.button?.color ||
        s360ConfigService.config?.configDetails?.primaryColor ||
        "#000",
      [config]
    );
    const configprimaryColor = useMemo(
      () =>
        config?.customDesign?.button?.backgroundColor ||
        s360ConfigService.config?.configDetails?.primaryFontColor ||
        "#fff",
      [config]
    );

    const handleCheckboxChange = useCallback(
      (event) => {
        onChange(event, text);
      },
      [onChange, text]
    );

    return (
      <StyledFormControlLabel
        disabled={disabled}
        label={text}
        config={config}
        configPrimaryFontColor={configPrimaryFontColor}
        configprimaryColor={configprimaryColor}
        control={
          <StyledCheckbox
            size="medium"
            checked={selected}
            onChange={handleCheckboxChange}
            disabled={disabled}
            configPrimaryFontColor={configPrimaryFontColor}
          />
        }
      />
    );
  },
  (prevProps, nextProps) =>
    prevProps.text === nextProps.text &&
    prevProps.componentId === nextProps.componentId &&
    prevProps.selected === nextProps.selected &&
    prevProps.disabled === nextProps.disabled &&
    JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config)
);

const RadioButtonComponent = React.memo(
  function RadioButtonComponent({
    text,
    componentId,
    selected,
    onChange,
    disabled,
    config,
    id,
  }) {
    const configPrimaryFontColor = useMemo(
      () =>
        config?.customDesign?.button?.color ||
        s360ConfigService.config?.configDetails?.primaryColor ||
        "#000",
      [config]
    );
    const configprimaryColor = useMemo(
      () =>
        config?.customDesign?.button?.backgroundColor ||
        s360ConfigService.config?.configDetails?.primaryFontColor ||
        "#fff",
      [config]
    );

    const handleRadioChange = useCallback(() => {
      onChange(text);
    }, [onChange, text]);

    return (
      <StyledFormControlLabel
        checked={selected}
        disabled={disabled}
        label={text}
        config={config}
        configPrimaryFontColor={configPrimaryFontColor}
        configprimaryColor={configprimaryColor}
        control={
          <StyledRadio
            size="medium"
            checked={selected}
            value={text}
            onChange={handleRadioChange}
            disabled={disabled}
            configPrimaryFontColor={configPrimaryFontColor}
          />
        }
      />
    );
  },
  (prevProps, nextProps) =>
    prevProps.text === nextProps.text &&
    prevProps.componentId === nextProps.componentId &&
    prevProps.selected === nextProps.selected &&
    prevProps.disabled === nextProps.disabled &&
    JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config)
);

const NCXButton = React.memo(
  function NCXButtonComponent(props) {
    const {
      buttons,
      subType,
      sendMessageToPreview,
      id,
      disabled: propDisabled,
      ticketId,
      engagementId,
      downloadInProgress,
    } = props;

    const [buttonsDisable, setButtonsDisabled] = useState(propDisabled);

    const [visibleButtons, setVisibleButtons] = useState([]);

    const [selectedCheckboxes, setSelectedCheckboxes] = useState([]);

    const [radioValue, setRadioValue] = useState(null);

    useEffect(() => {
      setButtonsDisabled(propDisabled);
    }, [propDisabled]);

    useEffect(() => {
      if (subType === "default" || !subType) {
        if (buttons && buttons.length > 0) {
          buttons.forEach((button, index) => {
            setTimeout(() => {
              setVisibleButtons((prevVisibleButtons) => [
                ...prevVisibleButtons,
                button.componentId,
              ]);
            }, index * 150);
          });
        }
      } else {
        if (buttons && buttons.length > 0) {
          setVisibleButtons(buttons.map((button) => button.componentId));
        } else {
          setVisibleButtons([]);
        }
      }
      return () => {};
    }, [buttons, subType]);

    const sendMessage = useCallback(
      (text, componentId) => {
        setButtonsDisabled(true);
        sendMessageToPreview(text, componentId);
      },
      [sendMessageToPreview]
    );

    const handleCheckboxChange = useCallback((event, text) => {
      setSelectedCheckboxes((prev) => {
        if (event.target.checked) {
          return [...prev, text];
        } else {
          return prev.filter((item) => item !== text);
        }
      });
    }, []);

    const handleRadioChange = useCallback((value) => {
      setRadioValue(value);
    }, []);

    const handleSubmit = useCallback(() => {
      if (selectedCheckboxes.length > 0) {
        const componentId =
          buttons && buttons.length > 0 ? buttons[0].componentId : undefined;
        const selectedText = selectedCheckboxes.join(", ");
        setButtonsDisabled(true);
        sendMessageToPreview(selectedText, componentId);
      }
    }, [sendMessageToPreview, buttons, selectedCheckboxes]);

    const handleRadioSubmit = useCallback(() => {
      if (radioValue) {
        const selectedButton = buttons
          ? buttons.find((button) => button.text === radioValue)
          : undefined;
        const componentId = selectedButton?.componentId;

        if (componentId) {
          setButtonsDisabled(true);
          sendMessageToPreview(radioValue, componentId);
        }
      }
    }, [sendMessageToPreview, buttons, radioValue]);

    if (subType === "checkbox") {
      return (
        <FormControl
          className="buttonGroupContainer"
          disabled={buttonsDisable}
          key={`${id}-buttonformcontrollercontainercheckbox`}
        >
          <FormGroup key={`${id}-buttonformgroupcontainercheckbox`}>
            {buttons &&
              buttons.map((buttonDetails, index) => (
                <CheckboxButton
                  {...buttonDetails}
                  selected={selectedCheckboxes.includes(buttonDetails.text)}
                  onChange={handleCheckboxChange}
                  disabled={buttonsDisable}
                  config={props.config}
                  key={`${id}-buttonformgroupcheckbox-${index}`}
                />
              ))}
          </FormGroup>
          <StyledSubmitButton
            onClick={handleSubmit}
            variant="contained"
            disabled={buttonsDisable || selectedCheckboxes.length === 0}
            key={`${id}-buttonmbuttoncheckboxsubmit`}
            config={props.config}
          >
            Submit
          </StyledSubmitButton>
        </FormControl>
      );
    }

    if (subType === "radio") {
      return (
        <FormControl
          className="buttonGroupContainer"
          disabled={buttonsDisable}
          key={`${id}-buttonformcontrollercontainerradio`}
        >
          <RadioGroup
            value={radioValue}
            onChange={(event) => handleRadioChange(event.target.value)}
            key={`${id}-buttonformgroupcontainerradio`}
          >
            {buttons &&
              buttons.map((buttonDetails, index) => (
                <RadioButtonComponent
                  {...buttonDetails}
                  selected={radioValue === buttonDetails.text}
                  onChange={setRadioValue}
                  value={buttonDetails.text}
                  disabled={buttonsDisable}
                  config={props.config}
                  key={`${id}-buttonformgroupradio-${index}`}
                />
              ))}
          </RadioGroup>
          <StyledSubmitButton
            onClick={handleRadioSubmit}
            variant="contained"
            disabled={buttonsDisable || radioValue === null}
            key={`${id}-buttonmbuttonradiosubmit`}
            config={props.config}
          >
            Submit
          </StyledSubmitButton>
        </FormControl>
      );
    }

    if (subType === "feedback") {
      return (
        <Box
          className="buttonContainer feedback-buttons"
          display="flex"
          gap={2}
          justifyContent="center"
          key={`${id}-buttoncontainer-feedback`}
        >
          {buttons &&
            buttons.slice(0, 2).map((buttonDetails, index) => {
              const isThumbsUp = index === 0;
              const iconColor = buttonsDisable
                ? "#b3b3b3"
                : isThumbsUp
                ? "#28a745"
                : "#6c757d";

              return (
                <StyledFeedbackButton
                  onClick={() => {
                    sendMessage(buttonDetails.text, buttonDetails.componentId);
                    if (!s360ConfigService.config?.preview) {
                      apiService.sendFaqFeedback(
                        buttonDetails.text,
                        buttonDetails.text,
                        engagementId,
                        ticketId,
                        s360ConfigService.config?.widgetconfigId
                      );
                    }
                  }}
                  disabled={buttonsDisable}
                  key={`${id}-buttonfeedback-${index}`}
                >
                  {isThumbsUp ? (
                    <ThumbUp sx={{ color: iconColor, marginRight: "8px" }} />
                  ) : (
                    <ThumbDown sx={{ color: iconColor, marginRight: "8px" }} />
                  )}
                  {buttonDetails.text}
                </StyledFeedbackButton>
              );
            })}
        </Box>
      );
    }

    return (
      <Box
        className="buttonContainer"
        display="flex"
        flexWrap="wrap"
        gap={1}
        key={`${id}-buttoncontainer-default`}
      >
        {buttons &&
          buttons.map((buttonDetails, index) => (
            <DefaultButton
              {...buttonDetails}
              visible={
                downloadInProgress
                  ? true
                  : subType === "default" || !subType
                  ? visibleButtons.includes(buttonDetails.componentId)
                  : true
              }
              sendMessage={sendMessage}
              disabled={buttonsDisable}
              config={props.config}
              key={`${id}-buttondefault-${index}`}
            />
          ))}
      </Box>
    );
  },
  (prevProps, nextProps) =>
    prevProps.disabled === nextProps.disabled &&
    prevProps.subType === nextProps.subType &&
    prevProps.id === nextProps.id &&
    prevProps.sendMessageToPreview === nextProps.sendMessageToPreview &&
    JSON.stringify(prevProps.buttons) === JSON.stringify(nextProps.buttons) &&
    JSON.stringify(prevProps.config) === JSON.stringify(nextProps.config)
);

export default NCXButton;
